import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import {
	Distributor,
	Role,
	Space,
	UpdateUser,
	useApi,
	UserScope,
} from "@soft-db/monitoring-api-client";
import { computed } from "vue";
import { useIsPublic } from "../plugins";
import { distributorsKeys } from "./distributor-query";
import { useQueryEnabled } from "./query";
import { spacesKeys } from "./space-query";

export const currentUserKeys = {
	root: ["current-user"] as const,
};

export function useCurrentUserInvalidation() {
	const cache = useQueryCache();

	return () => {
		cache.invalidateQueries({ key: currentUserKeys.root });
	};
}

export function useCurrentUser() {
	const api = useApi().currentUser();

	const { data: currentUser, ...rest } = useQuery({
		key: currentUserKeys.root,
		query: () => api.fetch(),
		enabled: useQueryEnabled(),
	});

	const hasRole = (requiredRole: Role) => {
		const user = currentUser.value;
		if (!user) return false;

		if (user.role === "sys_admin") return true;

		const allow: Role[] = [];

		switch (user.role) {
			case "admin":
				allow.push("admin");
			// falls through
			case "editor":
				allow.push("editor");
			// falls through
			case "viewer":
				allow.push("viewer");
		}

		return allow.includes(requiredRole);
	};

	const isPublic = computed(() => {
		return currentUser.value?.auth.type === "public";
	});

	return {
		currentUser,
		hasRole,
		isPublic,
		data: currentUser,
		...rest,
	};
}

export type CurrentUserUpdateOptions = { file?: File } & UpdateUser;

export function useUpdateCurrentUserAvatar() {
	const api = useApi().currentUser();

	return useMutation({
		mutation: (image: File) => api.updateAvatar(image),
		onSuccess: useCurrentUserInvalidation(),
	});
}

export function useDeleteCurrentUserAvatar() {
	const api = useApi().currentUser();

	return useMutation({
		mutation: () => api.deleteAvatar(),
		onSuccess: useCurrentUserInvalidation(),
	});
}

export function useUpdateCurrentUser() {
	const api = useApi().currentUser();

	return useMutation({
		mutation: (props: Partial<UpdateUser>) => api.update(props),
		onSuccess: useCurrentUserInvalidation(),
	});
}

export function useUpdateCurrentUserEmail() {
	const api = useApi().currentUser();

	return useMutation({
		mutation: (email: string) => api.updateEmail(email),
		onSuccess: useCurrentUserInvalidation(),
	});
}

export function useCurrentUserRole() {
	const { data } = useCurrentUser();

	const hasRole = (requiredRole: Role) => {
		const user = data.value;
		if (!user) return false;

		if (user.role === "sys_admin") return true;

		const allow: Role[] = [];

		switch (user.role) {
			case "admin":
				allow.push("admin");
			// falls through
			case "editor":
				allow.push("editor");
			// falls through
			case "viewer":
				allow.push("viewer");
		}

		return allow.includes(requiredRole);
	};

	return {
		role: computed(() => data.value?.role),
		isUser: computed(() => data.value?.role === "viewer"),
		isEditor: computed(() => data.value?.role === "editor"),
		isAdmin: computed(() => data.value?.role === "admin"),
		isSenator: computed(() => data.value?.role === "sys_admin"),
		hasRole,
	};
}

export type UserScopeWithDetails = { id: string } & (
	| {
			type: "distributor";
			details: Distributor;
	  }
	| {
			type: "space";
			details: Space;
	  }
	| {
			type: "none";
	  }
);

export function useScopeDetails(_scope: MaybeRefOrGetter<UserScope>) {
	const scope = toRef(_scope);

	const api = useApi();

	const queryStrategies = {
		distributor: (id: string) => ({
			key: distributorsKeys.byId(id),
			query: () => api.distributor().fetch(id),
		}),
		space: (id: string) => ({
			key: spacesKeys.byId(id),
			query: () => api.space().fetch(id),
		}),
		none: () => ({
			key: [null],
			query: () => Promise.resolve(null),
		}),
	};

	const currentOptions = computed(() => {
		return queryStrategies[scope.value.type](scope.value.id);
	});

	const { data, ...rest } = useQuery<Distributor | Space | null>({
		key: () => currentOptions.value.key,
		query: () => currentOptions.value.query(),
		enabled: useQueryEnabled(() => scope.value.type !== "none"),
	});

	return {
		scope: computed<UserScopeWithDetails | null>(() => {
			if (data.value == null) {
				return null;
			}

			// We need to cast, since there is no good way to
			// assert that the data match a space or an org.
			switch (scope.value.type) {
				case "distributor":
					return {
						type: "distributor",
						id: scope.value.id,
						details: data.value as Distributor,
					};
				case "space":
					return {
						type: "space",
						id: scope.value.id,
						details: data.value as Space,
					};
				case "none":
					return {
						type: "none",
						id: "",
					};
			}
		}),
		...rest,
	};
}

export function useCurrentUserScope() {
	const { currentUser } = useCurrentUser();
	const { isPublic } = useIsPublic();

	const scope = computed<UserScope>(() => {
		if (currentUser.value == null || isPublic) {
			return {
				type: "none",
				id: "",
			};
		}

		return currentUser.value.scope;
	});

	return useScopeDetails(scope);
}
